<template>
  <swiper-base ref="swiper" class="slider" :items="reviews" cycle="full" @change="onChangeItem">
    <template v-slot:item="{ item }">
      <div class="slide slider__slide">
        <div class="review-card slide__content">
          <div class="review-card__rating">
            <span :class="getStarIconClass(1, item.score)"></span>
            <span :class="getStarIconClass(2, item.score)"></span>
            <span :class="getStarIconClass(3, item.score)"></span>
            <span :class="getStarIconClass(4, item.score)"></span>
            <span :class="getStarIconClass(5, item.score)"></span>
          </div>
          <a :href="item.link" target="_blank" class="review-card__header">{{ item.title }}</a>
          <div class="review-card__body review-card-body">
            <p class="review-card-body__content">{{ item.body }}</p>
          </div>
          <p class="review-card__author">{{ item.author }}</p>
        </div>
      </div>
    </template>

    <template #buttons>
      <div class="slider__side-gradient slider__side-gradient--left"></div>
      <div class="slider__side-gradient slider__side-gradient--right"></div>

      <template v-if="isMobileLayout">
        <u-icon class="slider__button slider__button--prev" name="left" @click="onSwipe('end')" />
        <u-icon
          class="slider__button slider__button--next"
          name="right"
          @click="onSwipe('start')"
        />
      </template>

      <template v-else>
        <u-icon-button
          class="slider__button slider__button--prev"
          icon-name="left"
          size="m"
          kind="secondary"
          @click="onSwipe('end')"
        />
        <u-icon-button
          class="slider__button slider__button--next"
          icon-name="right"
          size="m"
          kind="secondary"
          @click="onSwipe('start')"
        />
      </template>
    </template>
  </swiper-base>
</template>

<script>
import { UIcon, UIconButton } from 'universkin-shared';

import SwiperBase from '@/components/common/app-swiper/SwiperBase.vue';

export default {
  name: 'ReviewsSlider',
  components: {
    SwiperBase,
    UIcon,
    UIconButton
  },
  props: {
    reviews: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      currentItemIndex: 0
    };
  },
  computed: {
    totalSlideItems() {
      return this.reviews.length;
    },
    totalSlidesNumber() {
      return this.reviews.length;
    },
    isMobileLayout() {
      return this.$windowWidth.value < 960;
    },
    lastItemOffset() {
      const width = this.$windowWidth.value;

      if (width >= 1920) {
        return 4;
      }

      if (width >= 1152) {
        return 3;
      }

      if (width >= 960) {
        return 2;
      }

      return 0;
    }
  },
  methods: {
    onChangeItem(itemIndex) {
      this.currentItemIndex = itemIndex;
    },
    getStarIconClass(star, score) {
      return (star === 1 && score >= 1) || score >= star - 0.2
        ? 'icon-star-filled'
        : 'icon-star-empty';
    },
    onSwipe(direction) {
      const increment = direction === 'end' ? -1 : 1;
      const newValue = this.currentItemIndex + increment;
      const min = 0;
      const max = this.reviews.length - 1 - this.lastItemOffset;

      let next = newValue;

      if (newValue < min) {
        next = max;
      } else if (newValue > max) {
        next = min;
      }

      this.$refs.swiper.swipeToIndex(next);
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../../assets/scss/colors.scss';

.slider {
  --item-width: 100%;
  --item-gap: 0px;
  position: relative;

  &__slide {
    display: flex;
    justify-content: center;
    align-items: flex-start;
    min-height: 230px;
    margin: 0 auto;
  }

  &__button {
    --u-icon-size: 20px;
    --u-icon-color: var(--u-color-graphite);
    position: absolute;
    top: 40%;
    transform: translateY(-50%);
    cursor: pointer;

    &:lang(ar) {
      transform: translateY(-50%) scaleX(-1);
    }

    &--prev:not(:lang(ar)),
    &--next:lang(ar) {
      left: 0;
    }

    &--next:not(:lang(ar)),
    &--prev:lang(ar) {
      right: 0;
    }
  }

  &__side-gradient {
    position: absolute;
    width: 20px;
    height: 100%;
    top: 0;

    &--left {
      background: linear-gradient(to right, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0) 90%);
      left: 0;
    }

    &--right {
      background: linear-gradient(to left, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0) 90%);
      right: 0;
    }
  }
}

.slide {
  &__content {
    max-width: 284px;
  }
}

.review-card {
  color: $tuatara-color;

  &__rating {
    display: flex;
    justify-content: center;
    margin-bottom: 20px;
  }

  &__header {
    display: block;
    max-height: 52px;
    height: 100%;
    margin-bottom: 24px;
    font-size: 14px;
    line-height: 17px;
    font-weight: bold;
    text-align: center;
    text-decoration: underline;
    color: $tuatara-color;
  }

  &__body {
    max-height: 72px;
    height: 100%;
    margin-bottom: 24px;
    font-size: 14px;
    line-height: 18px;
    text-indent: 5px;
  }

  &__author {
    font-size: 14px;
    line-height: 17px;
    color: $concord-color;
    text-align: center;
  }
}

.review-card-body {
  &__content {
    padding: 0 5px;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 4;
    line-clamp: 4;
    overflow: hidden;
    text-align: center;
    text-overflow: ellipsis;
  }
}

.icon-star {
  width: 20px;
  height: 20px;
}

.icon-star-filled {
  background-image: url(~@/assets/images/trustpilot-reviews/star-filled.svg);
}

.icon-star-empty {
  background-image: url(~@/assets/images/trustpilot-reviews/star-empty.svg);
}

.icon-star-filled,
.icon-star-empty {
  width: 20px;
  height: 20px;
  background-repeat: no-repeat;
}

@media (min-width: 960px) {
  .slider {
    --item-width: 33%;

    &__slide {
      min-height: 220px;
    }
  }

  .slide {
    &__content {
      max-width: 170px;
    }
  }

  .review-card {
    &__header {
      font-size: 16px;
      line-height: 18px;
    }

    &__body {
      max-height: 55px;
      height: 100%;
      font-size: 12px;
      line-height: 14px;
    }
  }
}

@media (min-width: 1152px) {
  .slider {
    --item-width: 25%;
  }

  .slider__slide:hover .review-card__header {
    text-decoration: underline;
  }

  .review-card {
    &__header {
      font-size: 16px;
      line-height: 18px;
      text-decoration: none;
    }

    &__body {
      max-height: 68px;
      height: 100%;
      font-size: 14px;
      line-height: 17px;
    }
  }
}

@media (min-width: 1920px) {
  .review-card {
    &__body {
      max-height: 68px;
      height: 100%;
      font-size: 14px;
      line-height: 17px;
    }
  }

  .slider {
    --item-width: 20%;

    &__slide {
      min-height: 225px;
    }
  }
}
</style>
